import { FACILITY_ROLE, UserRoles } from '../../shared/constants';

/** Analytics Engine API role strings — shared by chat and document upload. */
export const ANALYTICS_ENGINE_ROLES = {
  CUSTOMER_CARE: 'CUSTOMER_CARE',
  ENG: 'ENG',
  LOCATION_ADMIN: 'LOCATION_ADMIN',
  LOCATION_SCHEDULER: 'LOCATION_SCHEDULER',
  LOCATION_CAREGIVER: 'LOCATION_CAREGIVER',
  UNKNOWN: 'UNKNOWN',
} as const;

type DashboardUserLike = {
  privilege_id?: number;
  facility_role_id?: number | null;
  parent_facility_role_id?: number | null;
};

/** Maps document permission UI roles → Analytics Engine allowed_roles values. */
export const UI_ROLE_TO_ANALYTICS_ENGINE_ROLE: Partial<
  Record<UserRoles, string>
> = {
  [UserRoles.CUSTOMER_CARE]: ANALYTICS_ENGINE_ROLES.CUSTOMER_CARE,
  [UserRoles.ENGINEERING]: ANALYTICS_ENGINE_ROLES.ENG,
  [UserRoles.FACILITY_ADMIN]: ANALYTICS_ENGINE_ROLES.LOCATION_ADMIN,
  [UserRoles.FACILITY_SCHEDULER]: ANALYTICS_ENGINE_ROLES.LOCATION_SCHEDULER,
};

export const mapPermissionsToApiRoles = (permissions: UserRoles[]): string[] =>
  permissions
    .map((role) => UI_ROLE_TO_ANALYTICS_ENGINE_ROLE[role])
    .filter((role): role is string => Boolean(role));

/** Maps logged-in user to the role string sent to chat_initiate. */
export const resolveChatbotApiRole = (
  dashboardUserInfo?: DashboardUserLike | null,
): string => {
  if (!dashboardUserInfo) {
    return ANALYTICS_ENGINE_ROLES.UNKNOWN;
  }

  const { privilege_id, facility_role_id, parent_facility_role_id } =
    dashboardUserInfo;

  if (privilege_id === 2) {
    return ANALYTICS_ENGINE_ROLES.CUSTOMER_CARE;
  }

  if (privilege_id === 3 || privilege_id === 4) {
    return ANALYTICS_ENGINE_ROLES.ENG;
  }

  if (privilege_id === 1) {
    if (parent_facility_role_id === FACILITY_ROLE.FACILITY_ADMIN) {
      return ANALYTICS_ENGINE_ROLES.LOCATION_ADMIN;
    }

    switch (facility_role_id) {
      case FACILITY_ROLE.FACILITY_ADMIN:
        return ANALYTICS_ENGINE_ROLES.LOCATION_ADMIN;
      case FACILITY_ROLE.FACILITY_SCHEDULER:
        return ANALYTICS_ENGINE_ROLES.LOCATION_SCHEDULER;
      case FACILITY_ROLE.FACILITY_CAREGIVER:
      case FACILITY_ROLE.FACILITY_PATIENT:
      case FACILITY_ROLE.FACILITY_NURSE:
        return ANALYTICS_ENGINE_ROLES.LOCATION_CAREGIVER;
      default:
        return ANALYTICS_ENGINE_ROLES.LOCATION_CAREGIVER;
    }
  }

  return ANALYTICS_ENGINE_ROLES.UNKNOWN;
};

const CHATBOT_ALLOWED_API_ROLES: readonly string[] = [
  ANALYTICS_ENGINE_ROLES.CUSTOMER_CARE,
  ANALYTICS_ENGINE_ROLES.LOCATION_ADMIN,
];

export const canAccessDocuments = (
  dashboardUserInfo: DashboardUserLike | null | undefined,
): boolean =>
  resolveChatbotApiRole(dashboardUserInfo) ===
  ANALYTICS_ENGINE_ROLES.CUSTOMER_CARE;

export const canShowChatbot = (
  dashboardUserInfo: DashboardUserLike | null | undefined,
  token: string,
  customerId: string,
): boolean => {
  if (!token || !customerId || !dashboardUserInfo) {
    return false;
  }

  const role = resolveChatbotApiRole(dashboardUserInfo);
  return CHATBOT_ALLOWED_API_ROLES.includes(role);
};
